import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { db } from '../db/firebase.js';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';

const OWNER = 'digital-crab';
const REPO = 'yellow-mythics';
const WORKFLOW = 'mythic_cron.yml';
const POLL_INTERVAL = 10000;

const CronContext = createContext(null);

export const useCronContext = () => {
    const ctx = useContext(CronContext);
    if (!ctx) throw new Error('useCronContext must be used inside CronProvider');
    return ctx;
};

export const logActivity = async (action, detail) => {
    try {
        const ref = doc(db, 'config', 'activityLog');
        await updateDoc(ref, {
            entries: arrayUnion({ ts: new Date().toISOString(), action, detail }),
        });
    } catch {
        // activityLog doc may not exist yet — ignore silently
    }
};

export const CronProvider = ({ children }) => {
    const [runs, setRuns] = useState([]);
    const [loadingRuns, setLoadingRuns] = useState(true);
    const [token, setToken] = useState(null);
    const [tokenError, setTokenError] = useState(null);
    const pollRef = useRef(null);

    const isRunning = runs.length > 0 &&
        (runs[0].status === 'in_progress' || runs[0].status === 'queued');

    const getToken = useCallback(async () => {
        if (token) return token;
        try {
            const snap = await getDoc(doc(db, 'config', 'adminSecrets'));
            if (!snap.exists() || !snap.data().githubToken) {
                setTokenError('GitHub token not configured. See Cron Control tab for setup instructions.');
                return null;
            }
            const t = snap.data().githubToken;
            setToken(t);
            return t;
        } catch (err) {
            setTokenError('Failed to read GitHub token: ' + err.message);
            return null;
        }
    }, [token]);

    const fetchRuns = useCallback(async () => {
        try {
            const t = await getToken();
            const headers = t
                ? { Authorization: `Bearer ${t}`, Accept: 'application/vnd.github+json' }
                : { Accept: 'application/vnd.github+json' };
            const res = await fetch(
                `https://api.github.com/repos/${OWNER}/${REPO}/actions/workflows/${WORKFLOW}/runs?per_page=10`,
                { headers }
            );
            if (!res.ok) throw new Error(`GitHub API ${res.status}`);
            const data = await res.json();
            setRuns(data.workflow_runs || []);
        } catch (err) {
            console.error('CronContext: fetchRuns failed:', err.message);
        } finally {
            setLoadingRuns(false);
        }
    }, [getToken]);

    // Initial fetch
    useEffect(() => {
        fetchRuns();
    }, [fetchRuns]);

    // Auto-poll every 10s when a run is active
    useEffect(() => {
        if (isRunning) {
            pollRef.current = setInterval(fetchRuns, POLL_INTERVAL);
        } else {
            clearInterval(pollRef.current);
        }
        return () => clearInterval(pollRef.current);
    }, [isRunning, fetchRuns]);

    const triggerCron = useCallback(async () => {
        if (isRunning) {
            return { ok: false, message: 'A cron job is already running. It will pick up any new IDs on completion.' };
        }
        const t = await getToken();
        if (!t) {
            return { ok: false, message: tokenError || 'GitHub token not configured.' };
        }
        try {
            const res = await fetch(
                `https://api.github.com/repos/${OWNER}/${REPO}/actions/workflows/${WORKFLOW}/dispatches`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${t}`,
                        Accept: 'application/vnd.github+json',
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ ref: 'main' }),
                }
            );
            if (res.status === 204) {
                await logActivity('cron_triggered', 'Manual trigger via admin panel');
                setTimeout(fetchRuns, 4000);
                return { ok: true, message: 'Cron triggered! Watching for it to start...' };
            }
            const errData = await res.json().catch(() => ({}));
            return { ok: false, message: `GitHub API error (${res.status}): ${errData.message || 'Unknown'}` };
        } catch (err) {
            return { ok: false, message: 'Network error: ' + err.message };
        }
    }, [isRunning, getToken, tokenError, fetchRuns]);

    return (
        <CronContext.Provider value={{ runs, loadingRuns, isRunning, fetchRuns, triggerCron, tokenError }}>
            {children}
        </CronContext.Provider>
    );
};
